import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, MapPin, Send, Radio, Baby, ImagePlus, X, Trash2, Settings } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const TRIAGE = {
  high: "border-red-200 bg-red-50 text-[#D5281B]",
  medium: "border-amber-200 bg-amber-50 text-[#ED8B00]",
  low: "border-emerald-200 bg-emerald-50 text-[#007F3B]",
};

const fmtDay = (iso) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "";
const fmtTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};
const agoText = (iso) => {
  if (!iso) return "time unknown";
  const ago = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return ago < 1 ? "just now" : `${ago} min ago`;
};

const MARKER_COLORS = ["#D5281B", "#005EB8", "#007F3B", "#ED8B00", "#7C3AED", "#0F2547"];

const StatChip = ({ label, value, cls, testid }) => (
  <span className={`inline-flex items-baseline gap-1.5 rounded-lg border px-2.5 py-1.5 ${cls}`} data-testid={testid}>
    <span className="font-heading text-lg font-black tabular-nums leading-none">{value}</span>
    <span className="text-[9px] font-bold uppercase tracking-wider opacity-70">{label}</span>
  </span>
);

export default function ControlEvent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [w3wInfo, setW3wInfo] = useState({});
  const [pinning, setPinning] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [phoneLocations, setPhoneLocations] = useState([]);
  const [radioLocations, setRadioLocations] = useState([]);
  const [trackerMeta, setTrackerMeta] = useState({
    phone_sharing_enabled: true,
    radio_configured: false,
    radio_status: "loading",
    radio_message: "",
    radio_dispatcher: "",
    radio_user_count: 0,
  });
  const [trackerSettingsOpen, setTrackerSettingsOpen] = useState(false);
  const [trackerRefreshing, setTrackerRefreshing] = useState(false);
  const [addingMarker, setAddingMarker] = useState(false);
  const [markerDraft, setMarkerDraft] = useState(null);
  const [editMarker, setEditMarker] = useState(null);
  const [movingMarker, setMovingMarker] = useState(null);
  const [markerForm, setMarkerForm] = useState({ label: "", color: "#D5281B" });
  const trackerRef = useRef(null);
  const trackerMap = useRef(null);
  const trackerLayer = useRef(null);
  const radioLayer = useRef(null);
  const trackerFit = useRef("");
  const trackerFixSize = useRef(null);
  const trackerMarkers = useRef({});
  const radioMarkers = useRef({});
  const chatEnd = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/control/event/${id}`);
      setData(data);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/control-login");
      else if (err?.response?.status === 404) { toast.error("Event not found"); navigate("/control"); }
      else toast.error(formatApiError(err));
    }
  }, [id, navigate]);

  useEffect(() => {
    api.get("/control/status").then((r) => {
      if (!r.data.authenticated) navigate("/control-login");
    }).catch(() => navigate("/control-login"));
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load, navigate]);

  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.chat?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post(`/control/event/${id}/chat`, { text: msg });
      setMsg("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const locate = async (pid, words) => {
    try {
      const { data } = await api.get(`/control/w3w/convert`, { params: { words } });
      setW3wInfo((m) => ({ ...m, [pid]: data }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const mapUrl = `${process.env.REACT_APP_BACKEND_URL}/api/control/event/${id}/site-map`;

  const uploadMap = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/control/event/${id}/site-map`, fd);
      toast.success("Site plan saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleMapClick = async (e) => {
    if (!pinning && !addingMarker && !movingMarker) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 10000) / 100;
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 10000) / 100;
    try {
      if (movingMarker) {
        await api.patch(`/control/event/${id}/markers/${movingMarker}`, { x, y });
        toast.success("Marker moved");
        setMovingMarker(null);
      } else if (addingMarker) {
        setMarkerForm({ label: "", color: "#D5281B" });
        setMarkerDraft({ x, y });
        setAddingMarker(false);
        return;
      } else {
        await api.post(`/control/event/${id}/pin`, { patient_id: pinning, x, y });
        toast.success("Casualty pinned to the plan");
        setPinning(null);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveMarker = async (e) => {
    e.preventDefault();
    try {
      if (markerDraft) {
        await api.post(`/control/event/${id}/markers`, { ...markerForm, ...markerDraft });
        toast.success("Marker added");
        setMarkerDraft(null);
      } else if (editMarker) {
        await api.patch(`/control/event/${id}/markers/${editMarker.id}`, { label: markerForm.label, color: markerForm.color });
        toast.success("Marker updated");
        setEditMarker(null);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteMarker = async () => {
    try {
      await api.delete(`/control/event/${id}/markers/${editMarker.id}`);
      toast.success("Marker deleted");
      setEditMarker(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteMap = async () => {
    if (!window.confirm("Remove the site plan? Its markers and casualty pins go with it.")) return;
    try {
      await api.delete(`/control/event/${id}/site-map`);
      toast.success("Site plan removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const unpin = async (pid) => {
    try {
      await api.post(`/control/event/${id}/pin`, { patient_id: pid });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateTrackerMarkers = useCallback((radio, phone) => {
    const map = trackerMap.current;
    const phoneGroup = trackerLayer.current;
    const radioGroup = radioLayer.current;
    if (!map || !phoneGroup || !radioGroup) return;

    const pts = [];
    const seenRadio = new Set();
    radio.forEach((r) => {
      const key = r.uid;
      seenRadio.add(key);
      const tip = `${r.nick || r.uid} — radio — ${agoText(r.updated_at)}`;
      const existing = radioMarkers.current[key];
      if (existing) {
        existing.setLatLng([r.lat, r.lng]);
        existing.setTooltipContent(tip);
      } else {
        radioMarkers.current[key] = L.circleMarker([r.lat, r.lng],
          { radius: 9, color: "#007F3B", fillColor: "#10B981", fillOpacity: 0.95, weight: 2 })
          .bindTooltip(tip, { direction: "top" })
          .addTo(radioGroup);
      }
      pts.push([r.lat, r.lng]);
    });
    Object.keys(radioMarkers.current).forEach((uid) => {
      if (!seenRadio.has(uid)) {
        radioGroup.removeLayer(radioMarkers.current[uid]);
        delete radioMarkers.current[uid];
      }
    });

    const seenPhones = new Set();
    phone.forEach((l) => {
      seenPhones.add(l.name);
      const tip = `${l.name} — phone — ${agoText(l.updated_at)}`;
      const existing = trackerMarkers.current[l.name];
      if (existing) {
        existing.setLatLng([l.lat, l.lng]);
        existing.setTooltipContent(tip);
      } else {
        trackerMarkers.current[l.name] = L.circleMarker([l.lat, l.lng],
          { radius: 8, color: "#005EB8", fillColor: "#005EB8", fillOpacity: 0.9, weight: 2 })
          .bindTooltip(tip, { direction: "top" })
          .addTo(phoneGroup);
      }
      pts.push([l.lat, l.lng]);
    });
    Object.keys(trackerMarkers.current).forEach((name) => {
      if (!seenPhones.has(name)) {
        phoneGroup.removeLayer(trackerMarkers.current[name]);
        delete trackerMarkers.current[name];
      }
    });

    if (pts.length) {
      const sig = [
        ...radio.map((r) => `radio:${r.uid}`),
        ...phone.map((p) => `phone:${p.name}`),
      ].sort().join("|");
      if (sig !== trackerFit.current) {
        trackerFit.current = sig;
        if (pts.length === 1) map.setView(pts[0], 16);
        else map.fitBounds(pts, { padding: [30, 30], maxZoom: 15 });
      }
    } else {
      trackerFit.current = "";
    }
  }, []);

  const loadTracker = useCallback(async (manual = false) => {
    if (manual) setTrackerRefreshing(true);
    try {
      const { data: tracker } = await api.get("/control/tracker-locations");
      const radio = tracker.radio || [];
      const phone = tracker.phone || [];
      setTrackerMeta(tracker);
      setRadioLocations(radio);
      setPhoneLocations(phone);
      updateTrackerMarkers(radio, phone);
    } catch (e) {
      console.debug("Tracker poll failed; retrying on next interval:", e?.message || e);
      if (manual) toast.error(formatApiError(e));
    } finally {
      if (manual) setTrackerRefreshing(false);
    }
  }, [updateTrackerMarkers]);

  const togglePhoneSharing = async () => {
    try {
      const enabled = !trackerMeta.phone_sharing_enabled;
      const { data: result } = await api.post("/control/radio/settings", { enabled });
      toast.success(result.message);
      await loadTracker(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    if (!trackerRef.current || trackerMap.current) return;
    const map = L.map(trackerRef.current, { attributionControl: false }).setView([52.13, -3.78], 7);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    trackerMap.current = map;
    radioLayer.current = L.layerGroup().addTo(map);
    trackerLayer.current = L.layerGroup().addTo(map);
    const fixSize = () => map.invalidateSize();
    trackerFixSize.current = fixSize;
    setTimeout(() => { fixSize(); loadTracker(); }, 250);
    window.addEventListener("resize", fixSize);
  }, [data, loadTracker]);

  useEffect(() => () => {
    if (trackerFixSize.current) window.removeEventListener("resize", trackerFixSize.current);
    trackerMarkers.current = {};
    radioMarkers.current = {};
    trackerMap.current?.remove();
    trackerMap.current = null;
  }, []);

  useEffect(() => {
    loadTracker();
    const t = setInterval(() => loadTracker(), 15000);
    return () => clearInterval(t);
  }, [loadTracker]);

  const active = useMemo(() => (data?.patients || []).filter((p) => p.status !== "discharged"), [data]);
  const discharged = useMemo(() => (data?.patients || []).filter((p) => p.status === "discharged"), [data]);

  if (!data) {
    return <div className="min-h-screen bg-[#F4F7FA] grid place-items-center text-slate-500 text-sm">Loading…</div>;
  }

  const { event: ev, counts, patients, lost_children, chat } = data;

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="control-event-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-3 py-2 flex items-center gap-2 flex-wrap">
          <button onClick={() => navigate("/control")} data-testid="control-back-btn"
            className="h-9 w-9 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors shrink-0">
            <ChevronLeft className="h-5 w-5 text-[#0F2547]" />
          </button>
          <div className="min-w-0 mr-1">
            <h1 className="font-heading text-lg font-bold text-[#0F2547] truncate leading-tight" data-testid="control-event-title">{ev.name}</h1>
            <p className="text-[10px] text-slate-500 truncate">
              {ev.venue || "Venue TBC"} · {ev.event_date ? fmtDay(ev.event_date) : "No date"}
              {ev.end_date && ev.end_date > ev.event_date ? ` → ${fmtDay(ev.end_date)}` : ""}
              {ev.times ? ` · ${ev.times}` : ""}
              {ev.expected_attendance ? ` · ~${ev.expected_attendance} expected` : ""}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap ml-auto" data-testid="control-stats-panel">
            <StatChip label="With you" value={counts.active} cls="border-slate-200 bg-[#F4F7FA] text-[#005EB8]" testid="stat-active" />
            <StatChip label="High" value={counts.high} cls={`${counts.high > 0 ? "border-[#D5281B] bg-red-50" : "border-slate-200 bg-[#F4F7FA]"} text-[#D5281B]`} testid="stat-high" />
            <StatChip label="Out" value={counts.discharged} cls="border-slate-200 bg-[#F4F7FA] text-slate-500" testid="stat-discharged" />
            <StatChip label="Treated" value={counts.treated} cls="border-slate-200 bg-[#F4F7FA] text-[#007F3B]" testid="stat-treated" />
            <StatChip label="Lost" value={counts.lost_with_us} cls={`${counts.lost_with_us > 0 ? "border-[#ED8B00] bg-amber-50" : "border-slate-200 bg-[#F4F7FA]"} text-[#ED8B00]`} testid="stat-lost_with_us" />
          </div>
          <span className="hidden sm:flex items-center gap-1.5 text-[10px] text-slate-500 shrink-0">
            <span className="h-1.5 w-1.5 rounded-full bg-[#007F3B]" /> Live · 5s
          </span>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-3 grid grid-cols-1 lg:grid-cols-12 gap-3 lg:h-[calc(100vh-57px)] lg:overflow-hidden">
        {/* LEFT: site plan + staff tracker */}
        <div className="lg:col-span-6 flex flex-col gap-3 lg:min-h-0">
          <div className="bg-white rounded-2xl border border-slate-200 p-3 lg:flex-[2] lg:min-h-0 lg:overflow-y-auto" data-testid="control-map-panel">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-[#005EB8]" /> Site plan
              </p>
              <div className="flex items-center gap-1.5">
                {ev.site_map && (
                  <>
                    <button onClick={() => { setAddingMarker((v) => !v); setPinning(null); setMovingMarker(null); }}
                      data-testid="marker-add-toggle"
                      className={`h-8 px-3 rounded-lg border text-[11px] font-bold inline-flex items-center gap-1.5 transition-colors ${addingMarker ? "bg-[#005EB8] border-[#005EB8] text-white" : "border-slate-300 text-[#005EB8] hover:border-[#005EB8]"}`}>
                      <MapPin className="h-3.5 w-3.5" /> {addingMarker ? "Tap the plan…" : "Add marker"}
                    </button>
                    <button onClick={deleteMap} data-testid="map-delete"
                      className="h-8 px-3 rounded-lg border border-slate-300 text-[11px] font-bold text-slate-400 hover:text-[#D5281B] hover:border-[#D5281B] inline-flex items-center gap-1.5 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" /> Delete plan
                    </button>
                  </>
                )}
                <label className="h-8 px-3 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-[11px] font-bold inline-flex items-center gap-1.5 cursor-pointer transition-colors"
                  data-testid="map-upload-label">
                  <ImagePlus className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : ev.site_map ? "Replace" : "Upload plan"}
                  <input type="file" accept="image/*,.pdf" className="hidden" data-testid="map-upload-input"
                    onChange={uploadMap} disabled={uploading} />
                </label>
              </div>
            </div>
            {!ev.site_map ? (
              <p className="text-xs text-slate-400">Upload the event site plan (photo or PDF), then pin each casualty where they're being treated — tap "Pin to site plan" on a casualty, then tap the plan.</p>
            ) : (
              <>
                {pinning && (
                  <div className="mb-2 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 flex items-center justify-between" data-testid="pin-mode-bar">
                    <p className="text-xs font-bold text-[#005EB8]">Tap the plan where this casualty is…</p>
                    <button onClick={() => setPinning(null)} data-testid="pin-cancel"
                      className="text-xs font-bold text-slate-500 hover:text-[#D5281B] inline-flex items-center gap-1">
                      <X className="h-3 w-3" /> Cancel
                    </button>
                  </div>
                )}
                <div className="text-center">
                  <div className={`relative inline-block max-w-full rounded-xl overflow-hidden border border-slate-200 ${(pinning || addingMarker || movingMarker) ? "cursor-crosshair ring-2 ring-[#005EB8]" : ""}`}
                    onClick={handleMapClick} data-testid="site-map-canvas">
                    <img src={mapUrl} alt="Event site plan" className="block max-h-[30vh] w-auto max-w-full" data-testid="site-map-img" />
                    {(ev.map_markers || []).map((m) => (
                      <button key={m.id} type="button" title={m.label}
                        style={{ left: `${m.x}%`, top: `${m.y}%`, backgroundColor: m.color }}
                        className="group absolute -translate-x-1/2 -translate-y-1/2 h-4 w-4 rounded-full border-2 border-white shadow-md hover:scale-125 transition-transform"
                        data-testid={`map-marker-${m.id}`}
                        onClick={(e) => { e.stopPropagation(); setEditMarker(m); setMarkerForm({ label: m.label, color: m.color }); }}>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-1.5 py-0.5 rounded bg-[#0F2547] text-white text-[9px] font-bold whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
                          {m.label}
                        </span>
                      </button>
                    ))}
                    {active.filter((p) => p.map_pin).map((p) => (
                      <button key={p.id} type="button" title={p.name}
                        style={{ left: `${p.map_pin.x}%`, top: `${p.map_pin.y}%` }}
                        className="absolute -translate-x-1/2 -translate-y-full"
                        data-testid={`map-pin-${p.id}`}
                        onClick={(e) => { e.stopPropagation(); toast.info(`${p.name} — ${p.location || "location unset"}`); }}>
                        <MapPin fill="#fff" className={`h-7 w-7 drop-shadow-md ${p.triage === "high" ? "text-[#D5281B]" : p.triage === "medium" ? "text-[#ED8B00]" : "text-[#007F3B]"}`} />
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-3 shrink-0 lg:shrink lg:flex-[3] lg:min-h-0 flex flex-col" data-testid="control-tracker-panel">
            <div className="mb-2 flex items-start justify-between gap-2 shrink-0">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <Radio className="h-3.5 w-3.5 text-[#007F3B]" /> Radio tracker — Tram PTT
                </p>
                <p data-testid="radio-tracker-status"
                  className={`mt-1 text-[11px] font-semibold ${trackerMeta.radio_status === "live" ? "text-[#007F3B]" : trackerMeta.radio_status === "error" ? "text-[#D5281B]" : "text-slate-500"}`}>
                  {trackerMeta.radio_status === "not_configured" ? "Radio map not configured" :
                    trackerMeta.radio_status === "no_users" ? "Connected — no radio users returned" :
                    trackerMeta.radio_status === "no_locations" ? "Connected — no recent radio GPS fixes" :
                    trackerMeta.radio_status === "stale" ? "Connected — latest radio fix is stale" :
                    trackerMeta.radio_status === "error" ? "Radio map connection issue" :
                    trackerMeta.radio_status === "live" ? "Live radio locations" : "Loading radio map…"}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">

                <button type="button" onClick={() => setTrackerSettingsOpen((v) => !v)}
                  data-testid="radio-map-settings-btn" title="Map settings"
                  className={`h-8 w-8 grid place-items-center rounded-lg border transition-colors ${trackerSettingsOpen ? "bg-[#005EB8] border-[#005EB8] text-white" : "border-slate-300 text-[#005EB8] hover:border-[#005EB8]"}`}>
                  <Settings className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            {trackerSettingsOpen && (
              <div className="mb-2 rounded-xl border border-sky-200 bg-sky-50 p-3 space-y-2" data-testid="radio-map-settings">
                <p className="text-[11px] text-slate-600">
                  Tram dispatcher: <span className="font-bold text-[#0F2547]" data-testid="radio-dispatcher-name">{trackerMeta.radio_dispatcher || "not configured"}</span>
                  {" · "}{trackerMeta.radio_user_count} radio users visible
                </p>
                <button type="button" onClick={togglePhoneSharing} data-testid="phone-sharing-toggle"
                  className={`h-9 px-3 rounded-lg text-[11px] font-bold transition-colors ${trackerMeta.phone_sharing_enabled ? "bg-[#0F2547] text-white hover:bg-[#173766]" : "bg-[#007F3B] text-white hover:bg-[#006631]"}`}>
                  {trackerMeta.phone_sharing_enabled ? "Switch phone sharing off" : "Switch phone sharing on"}
                </button>
                <p className="text-[10px] text-slate-500">
                  The Tram API credentials are stored on the server only and are never sent to the browser. Switching phone sharing off hides the portal button and clears phone dots.
                </p>
              </div>
            )}
            <div ref={trackerRef} className="h-64 lg:h-auto lg:flex-1 lg:min-h-[240px] rounded-xl overflow-hidden border border-slate-200 relative z-0" data-testid="control-tracker-map" />
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5" data-testid="tracker-list">
              {radioLocations.map((r) => (
                <p key={`radio-${r.uid}`} className="text-[11px] text-slate-600" data-testid={`radio-row-${r.uid}`}>
                  <span className="font-semibold text-[#007F3B]">{r.nick || r.uid}</span>{" "}
                  <span className="text-slate-400">{agoText(r.updated_at)} · radio{r.accuracy ? ` · ±${Math.round(r.accuracy)}m` : ""}</span>
                </p>
              ))}
              {trackerMeta.phone_sharing_enabled && phoneLocations.map((l) => (
                <p key={`phone-${l.name}`} className="text-[11px] text-slate-600" data-testid={`tracker-row-${l.name}`}>
                  <span className="font-semibold">{l.name}</span>{" "}
                  <span className="text-slate-400">{agoText(l.updated_at)} · phone{l.accuracy ? ` · ±${Math.round(l.accuracy)}m` : ""}</span>
                </p>
              ))}
              {radioLocations.length === 0 && (
                <p className="text-[11px] text-slate-400" data-testid="radio-empty-message">
                  {trackerMeta.radio_message || "Waiting for radio locations…"}
                </p>
              )}
              {trackerMeta.phone_sharing_enabled && phoneLocations.length === 0 && (
                <p className="text-[11px] text-slate-400" data-testid="phone-empty-message">No phone locations currently shared.</p>
              )}
              {!trackerMeta.phone_sharing_enabled && (
                <p className="text-[11px] font-semibold text-slate-500" data-testid="phone-sharing-off-note">
                  Phone location sharing is off — radio units remain on the map.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* MIDDLE: casualties + lost children */}
        <div className="lg:col-span-3 flex flex-col gap-3 lg:min-h-0">
          <div className="bg-white rounded-2xl border border-slate-200 p-3 flex-[3] lg:min-h-0 flex flex-col" data-testid="control-patients-panel">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 shrink-0">
              Casualties on site ({active.length})
            </p>
            <div className="space-y-2 overflow-y-auto lg:flex-1 lg:min-h-0 max-h-[38vh] lg:max-h-none pr-1">
              {active.length === 0 && <p className="text-xs text-slate-400">No active casualties.</p>}
              {active.map((p) => (
                <div key={p.id}
                  className={`rounded-xl border px-2.5 py-2 ${p.triage === "high" ? "border-[#D5281B]/60 bg-red-50/60" : "border-slate-200 bg-[#F4F7FA]"}`}
                  data-testid={`control-patient-${p.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-bold text-[#0F2547] truncate">{p.name}</p>
                    <span className={`text-[9px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${TRIAGE[p.triage] || TRIAGE.low}`}>
                      {p.triage}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {p.location || "Location unset"}{p.arrived_at ? ` · in ${fmtTime(p.arrived_at)}` : ""}
                  </p>
                  {p.injuries.length > 0 && (
                    <p className="text-[11px] text-slate-400 mt-0.5 truncate">{p.injuries.join(", ")}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {p.w3w && (
                      <>
                        <a href={`https://what3words.com/${p.w3w}`} target="_blank" rel="noreferrer"
                          data-testid={`w3w-location-link-${p.id}`}
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-[#D5281B] border border-red-200 bg-white rounded-full px-2 py-0.5 hover:bg-red-50 transition-colors">
                          <MapPin className="h-3 w-3" /> ///{p.w3w}
                        </a>
                        <button onClick={() => locate(p.id, p.w3w)} data-testid={`w3w-locate-btn-${p.id}`}
                          className="text-[10px] font-semibold text-[#005EB8] hover:underline">
                          Locate
                        </button>
                      </>
                    )}
                    {ev.site_map && (
                      p.map_pin ? (
                        <button onClick={() => unpin(p.id)} data-testid={`pin-remove-${p.id}`}
                          className="text-[10px] font-semibold text-slate-400 hover:text-[#D5281B]">Remove pin</button>
                      ) : (
                        <button onClick={() => setPinning(p.id)} data-testid={`pin-set-${p.id}`}
                          className="text-[10px] font-semibold text-[#005EB8] hover:underline">Pin to site plan</button>
                      )
                    )}
                  </div>
                  {w3wInfo[p.id] && (
                    <p className="text-[10px] text-slate-500 mt-1" data-testid={`w3w-info-${p.id}`}>
                      {w3wInfo[p.id].latitude?.toFixed(5)}, {w3wInfo[p.id].longitude?.toFixed(5)}
                      {w3wInfo[p.id].nearest_place ? ` · near ${w3wInfo[p.id].nearest_place}` : ""}
                    </p>
                  )}
                </div>
              ))}
              {discharged.length > 0 && (
                <>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 pt-1.5">Signed out ({discharged.length})</p>
                  {discharged.map((p) => (
                    <div key={p.id} className="rounded-xl border border-slate-200 bg-white px-2.5 py-1.5" data-testid={`control-patient-out-${p.id}`}>
                      <p className="text-xs text-slate-500">{p.name} <span className="text-slate-400">· {p.discharge_outcome || "discharged"}</span></p>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-3 flex-[2] lg:min-h-0 flex flex-col" data-testid="control-lost-panel">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 shrink-0 flex items-center gap-1.5">
              <Baby className="h-3.5 w-3.5 text-[#ED8B00]" /> Lost children ({counts.lost_with_us} with us)
            </p>
            <div className="space-y-1.5 overflow-y-auto lg:flex-1 lg:min-h-0 max-h-[20vh] lg:max-h-none pr-1">
              {lost_children.length === 0 && <p className="text-xs text-slate-400">None logged for this event.</p>}
              {lost_children.map((l) => (
                <div key={l.id}
                  className={`rounded-lg border px-2.5 py-1.5 ${l.status === "with_us" ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50/50"}`}
                  data-testid={`control-lost-${l.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-bold text-[#0F2547] truncate">{l.name || "Child"}</p>
                    <span className={`text-[9px] font-bold uppercase tracking-wider shrink-0 ${l.status === "with_us" ? "text-[#ED8B00]" : "text-[#007F3B]"}`}>
                      {l.status === "with_us" ? "With us" : "Reunited"}
                    </span>
                  </div>
                  {(l.description || l.clothing) && (
                    <p className="text-[10px] text-slate-500 truncate">{[l.description, l.clothing].filter(Boolean).join(" · ")}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: dispatch channel */}
        <div className="lg:col-span-3 lg:min-h-0">
          <div className="bg-white rounded-2xl border border-slate-200 p-3 flex flex-col lg:h-full" data-testid="control-chat-panel">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 shrink-0 flex items-center gap-1.5">
              <Radio className="h-3.5 w-3.5 text-[#005EB8]" /> Dispatch channel
            </p>
            <div className="flex-1 space-y-2 overflow-y-auto max-h-[40vh] lg:max-h-none lg:min-h-0 pr-1 mb-2" data-testid="control-chat-feed">
              {chat.length === 0 && <p className="text-xs text-slate-400">No messages on this channel yet.</p>}
              {chat.map((m) => (
                <div key={m.id}
                  className={`rounded-lg px-3 py-2 border ${m.kind === "alert" ? "border-red-200 bg-red-50" : m.sender_name === "CONTROL" ? "border-sky-200 bg-sky-50" : "border-slate-200 bg-[#F4F7FA]"}`}>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 flex justify-between">
                    <span className={m.kind === "alert" ? "text-[#D5281B] font-bold" : m.sender_name === "CONTROL" ? "text-[#005EB8] font-bold" : "font-semibold"}>
                      {m.kind === "alert" ? "⚠ " : ""}{m.sender_name}
                    </span>
                    <span>{fmtTime(m.created_at)}</span>
                  </p>
                  <p className="text-xs text-slate-700 mt-0.5 whitespace-pre-wrap">{m.text}</p>
                </div>
              ))}
              <div ref={chatEnd} />
            </div>
            <form onSubmit={send} className="flex gap-2 shrink-0">
              <input value={msg} onChange={(e) => setMsg(e.target.value)}
                placeholder="Message staff as CONTROL…" data-testid="control-chat-input"
                className="flex-1 h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white" />
              <button type="submit" disabled={sending || !msg.trim()} data-testid="control-chat-send-button"
                className="h-11 w-11 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white grid place-items-center transition-colors shrink-0">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </main>

      {(markerDraft || editMarker) && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4"
          onClick={() => { setMarkerDraft(null); setEditMarker(null); }} data-testid="marker-dialog-backdrop">
          <form onSubmit={saveMarker} className="bg-white rounded-2xl p-4 w-full max-w-sm space-y-3 shadow-xl"
            onClick={(e) => e.stopPropagation()} data-testid="marker-dialog">
            <p className="font-heading text-lg font-bold text-[#0F2547]">{editMarker ? "Edit marker" : "New marker"}</p>
            <input required value={markerForm.label} onChange={(e) => setMarkerForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="What is it? e.g. First aid post, Gate B, Welfare" data-testid="marker-label"
              className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <div>
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Colour</p>
              <div className="flex items-center gap-2 flex-wrap">
                {MARKER_COLORS.map((c) => (
                  <button type="button" key={c} onClick={() => setMarkerForm((f) => ({ ...f, color: c }))}
                    style={{ backgroundColor: c }} data-testid={`marker-color-${c.replace("#", "")}`}
                    className={`h-8 w-8 rounded-full border-2 transition-transform ${markerForm.color === c ? "border-[#0F2547] scale-110" : "border-white shadow"}`} />
                ))}
                <input type="color" value={markerForm.color} onChange={(e) => setMarkerForm((f) => ({ ...f, color: e.target.value }))}
                  data-testid="marker-color-custom" title="Pick any colour"
                  className="h-8 w-10 rounded cursor-pointer border border-slate-300" />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button type="submit" data-testid="marker-save"
                className="flex-1 h-11 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-bold">
                {editMarker ? "Save changes" : "Place marker"}
              </button>
              {editMarker && (
                <>
                  <button type="button" onClick={() => { setMovingMarker(editMarker.id); setEditMarker(null); toast("Tap the plan where it should go"); }}
                    data-testid="marker-move"
                    className="h-11 px-3 rounded-lg border border-slate-300 text-xs font-bold text-[#005EB8] hover:border-[#005EB8] transition-colors">
                    Move
                  </button>
                  <button type="button" onClick={deleteMarker} data-testid="marker-delete"
                    className="h-11 px-3 rounded-lg border border-slate-300 text-xs font-bold text-[#D5281B] hover:border-[#D5281B] transition-colors">
                    Delete
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
